import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  FiArrowLeft, FiSave, FiEye, FiSend, FiTrash2, FiEdit2, FiAlertTriangle, FiCheckSquare, FiSquare,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, Badge, Modal, ConfirmDialog, Spinner } from '../../components/common';
import CmsField from '../cms/components/CmsField';
import ClientSearchAutocomplete from './components/ClientSearchAutocomplete';
import ClientFormModal from './components/ClientFormModal';
import InvoiceLineItemsTable, { lineKey } from './components/InvoiceLineItemsTable';
import InvoiceTotalsPanel from './components/InvoiceTotalsPanel';
import InvoicePdfFrame from './components/InvoicePdfFrame';
import { invoiceService } from '../../services/invoiceService';
import { billingClientService } from '../../services/billingClientService';
import { billingSettingsService } from '../../services/billingSettingsService';
import { getApiError, getApiFieldErrors } from '../../services/cmsService';
import { addDaysISO, formatINR, splitRowErrors, todayISO } from './billingUtils';
import './Billing.css';
import './InvoiceEditorPage.css';

const DEFAULTS = { dueDays: 7, hsn: '998391', gstRate: '18' };
const NO_ERRORS = { fields: {}, rows: {} };

const emptyForm = () => ({ invoiceDate: todayISO(), dueDate: '', dueTouched: false, gstRate: '', notes: '' });

const projectLine = (project, defaultHsn) => ({
  key: lineKey(),
  projectId: project.id,
  description: project.description,
  designCode: project.designCode,
  hsnCode: project.hsnCode || defaultHsn,
  price: project.taxableAmount,
});

const lineFromInvoiceItem = (item, key) => ({
  key: key ?? lineKey(),
  projectId: item.projectId,
  description: item.description,
  designCode: item.designCode,
  hsnCode: item.hsnCode,
  price: item.price,
});

/** What "unsaved" is measured against. Local row keys are not content. */
const snapshotOf = (clientId, form, items) =>
  JSON.stringify({
    clientId: clientId ?? null,
    invoiceDate: form.invoiceDate,
    dueDate: form.dueTouched ? form.dueDate : null,
    gstRate: form.gstRate,
    notes: form.notes,
    items: items.map(({ key, ...rest }) => rest),
  });

const isAbort = (error) => error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';

/** A client the API can no longer return (deleted) — rebuilt from the invoice snapshot. */
const clientFromInvoice = (inv) => ({
  id: inv.clientId,
  name: inv.clientName,
  gstin: inv.clientGstin,
  address: inv.clientAddress,
  clientCode: inv.clientCode,
  stateCode: inv.clientStateCode,
  stateName: '',
  pendingProjects: [],
  unavailable: true,
});

const InvoiceEditorPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();
  const clientIdParam = searchParams.get('client_id');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [defaults, setDefaults] = useState(DEFAULTS);
  const [invoice, setInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [changingClient, setChangingClient] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState([]);
  const [savedSnapshot, setSavedSnapshot] = useState(() => snapshotOf(null, emptyForm(), []));
  const [errors, setErrors] = useState(NO_ERRORS);

  const [totals, setTotals] = useState(null);
  const [totalsLoading, setTotalsLoading] = useState(false);
  const [totalsError, setTotalsError] = useState('');

  const [busy, setBusy] = useState(null); // 'save' | 'preview' | 'issue' | 'delete' | 'client'
  const [clientModal, setClientModal] = useState(null); // { mode: 'create', name } | { mode: 'edit' }
  const [pendingSwitch, setPendingSwitch] = useState(null);
  const [confirmIssue, setConfirmIssue] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [leaveTarget, setLeaveTarget] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Set when a first save moves /new → /:id/edit, so that route change
  // doesn't reload what is already on screen.
  const skipLoadRef = useRef(null);

  const effectiveDueDate = form.dueTouched ? form.dueDate : addDaysISO(form.invoiceDate, defaults.dueDays);
  const snapshot = snapshotOf(client?.id, form, items);
  const isDirty = snapshot !== savedSnapshot;

  /* ---------------------------------------------------------------- *
   * Loading
   * ---------------------------------------------------------------- */

  useEffect(() => {
    billingSettingsService
      .get()
      .then(({ values }) => setDefaults({
        dueDays: Number(values.default_due_days ?? DEFAULTS.dueDays),
        hsn: values.default_hsn_code || DEFAULTS.hsn,
        gstRate: String(values.default_gst_rate ?? DEFAULTS.gstRate),
      }))
      .catch(() => {}); // the built-in defaults match the seeded settings
  }, []);

  const applyClient = useCallback((full, defaultHsn) => {
    setClient(full);
    setChangingClient(false);
    // Swap the project lines for the new client's pending projects, all ticked; keep ad-hoc lines.
    setItems((prev) => [
      ...prev.filter((line) => !line.projectId),
      ...full.pendingProjects.map((p) => projectLine(p, defaultHsn)),
    ]);
  }, []);

  useEffect(() => {
    if (isEdit && skipLoadRef.current === id) {
      skipLoadRef.current = null;
      return undefined;
    }

    let cancelled = false;
    const reset = () => {
      setInvoice(null);
      setClient(null);
      setChangingClient(false);
      setForm(emptyForm());
      setItems([]);
      setErrors(NO_ERRORS);
      setTotals(null);
      setSavedSnapshot(snapshotOf(null, emptyForm(), []));
    };

    const load = async () => {
      setLoading(true);
      reset();
      try {
        if (isEdit) {
          const inv = await invoiceService.getById(id);
          if (cancelled) return;
          if (!inv.permissions.canEdit) {
            toast.info('Issued invoices are locked. Cancel and re-issue to make corrections.');
            navigate(`/billing/invoices/${id}`, { replace: true });
            return;
          }
          const full = await billingClientService.getById(inv.clientId).catch(() => clientFromInvoice(inv));
          if (cancelled) return;

          const nextForm = {
            invoiceDate: inv.invoiceDate,
            dueDate: inv.dueDate,
            dueTouched: true,
            gstRate: '',
            notes: inv.notes,
          };
          // Only show an override when it differs from the default rate.
          const nextItems = inv.items.map((item) => lineFromInvoiceItem(item));
          setInvoice(inv);
          setClient(full);
          setForm(nextForm);
          setItems(nextItems);
          setSavedSnapshot(snapshotOf(full.id, nextForm, nextItems));
          setTotals({ ...inv, halfRate: null, stateMissing: !inv.taxType });
        } else if (clientIdParam) {
          const full = await billingClientService.getById(clientIdParam);
          if (cancelled) return;
          applyClient(full, DEFAULTS.hsn);
        }
      } catch (error) {
        if (cancelled) return;
        toast.error(getApiError(error, isEdit ? 'Failed to load the invoice' : 'Failed to load the client'));
        if (isEdit) navigate('/billing/invoices', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, clientIdParam]);

  // Show the saved GST override once the default rate is known.
  useEffect(() => {
    if (invoice && invoice.gstRate != null && Number(invoice.gstRate) !== Number(defaults.gstRate)) {
      setForm((prev) => (prev.gstRate === '' ? { ...prev, gstRate: String(Number(invoice.gstRate)) } : prev));
      setSavedSnapshot((prev) => {
        const parsed = JSON.parse(prev);
        return parsed.gstRate === '' ? JSON.stringify({ ...parsed, gstRate: String(Number(invoice.gstRate)) }) : prev;
      });
    }
    // Only when the loaded invoice or the defaults change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id, defaults.gstRate]);

  /* ---------------------------------------------------------------- *
   * Live totals — the server does all of the math
   * ---------------------------------------------------------------- */

  const pricesKey = items.map((line) => line.price).join('\u0001') + `#${items.length}`;

  useEffect(() => {
    if (!client) {
      setTotals(null);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setTotalsLoading(true);
      try {
        const next = await invoiceService.calculate(
          { clientId: client.id, invoiceId: invoice?.id, gstRate: form.gstRate, items },
          { signal: controller.signal }
        );
        setTotals(next);
        setTotalsError('');
      } catch (error) {
        if (isAbort(error)) return;
        setTotalsError(getApiError(error, 'Could not calculate the totals'));
      } finally {
        if (!controller.signal.aborted) setTotalsLoading(false);
      }
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `items` is read through pricesKey so typing a description doesn't recalculate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id, client?.stateCode, invoice?.id, form.gstRate, pricesKey]);

  /* ---------------------------------------------------------------- *
   * Unsaved-changes guard
   * ---------------------------------------------------------------- */

  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // BrowserRouter has no useBlocker, so the editor's own exits ask first.
  const leave = (to) => {
    if (isDirty) setLeaveTarget(to);
    else navigate(to);
  };

  /* ---------------------------------------------------------------- *
   * Client selection
   * ---------------------------------------------------------------- */

  const handleSelectClient = async (row) => {
    if (client && row.id === client.id) {
      setChangingClient(false);
      return;
    }
    setBusy('client');
    try {
      const full = await billingClientService.getById(row.id);
      if (items.some((line) => line.projectId)) setPendingSwitch(full);
      else applyClient(full, defaults.hsn);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to load the client'));
    } finally {
      setBusy(null);
    }
  };

  const handleClientSaved = async (saved) => {
    if (clientModal?.mode === 'edit') {
      // Refresh details (and the state, which decides the tax type); keep the lines.
      try {
        setClient(await billingClientService.getById(saved.id));
      } catch {
        setClient((prev) => ({ ...prev, ...saved, pendingProjects: prev.pendingProjects }));
      }
      return;
    }
    handleSelectClient(saved);
  };

  const toggleProject = (project) => {
    setItems((prev) =>
      prev.some((line) => line.projectId === project.id)
        ? prev.filter((line) => line.projectId !== project.id)
        : [...prev, projectLine(project, defaults.hsn)]
    );
  };

  const setAllProjects = (checked) => {
    const projects = client?.pendingProjects ?? [];
    setItems((prev) => {
      if (!checked) {
        const ids = new Set(projects.map((p) => p.id));
        return prev.filter((line) => !ids.has(line.projectId));
      }
      const present = new Set(prev.map((line) => line.projectId));
      return [...prev, ...projects.filter((p) => !present.has(p.id)).map((p) => projectLine(p, defaults.hsn))];
    });
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value, ...(field === 'dueDate' ? { dueTouched: true } : {}) }));
    setErrors((prev) => ({ ...prev, fields: { ...prev.fields, [field]: undefined } }));
  };

  /* ---------------------------------------------------------------- *
   * Save / preview / issue / delete
   * ---------------------------------------------------------------- */

  const save = async ({ quiet = false } = {}) => {
    if (!client) {
      toast.error('Select a client first');
      return null;
    }
    setErrors(NO_ERRORS);
    const payload = {
      clientId: client.id,
      invoiceDate: form.invoiceDate,
      dueDate: effectiveDueDate,
      gstRate: form.gstRate,
      notes: form.notes,
      items,
    };

    try {
      const saved = invoice
        ? await invoiceService.updateDraft(invoice.id, payload)
        : await invoiceService.createDraft(payload);

      // Adopt the server's normalised values (e.g. "126000" → "126000.00", blank HSN → default).
      const nextItems = saved.items.map((item, index) => lineFromInvoiceItem(item, items[index]?.key));
      const nextForm = { ...form, dueDate: saved.dueDate, dueTouched: true };
      setInvoice(saved);
      setItems(nextItems);
      setForm(nextForm);
      setSavedSnapshot(snapshotOf(client.id, nextForm, nextItems));

      if (!invoice) {
        skipLoadRef.current = String(saved.id);
        navigate(`/billing/invoices/${saved.id}/edit`, { replace: true });
      }
      if (!quiet) toast.success('Draft saved');
      return saved;
    } catch (error) {
      const split = splitRowErrors(getApiFieldErrors(error));
      setErrors(split);
      toast.error(getApiError(error, 'Failed to save the draft'));
      return null;
    }
  };

  const handleSave = async () => {
    setBusy('save');
    await save();
    setBusy(null);
  };

  const handlePreview = async () => {
    setBusy('preview');
    const target = isDirty || !invoice ? await save({ quiet: true }) : invoice;
    setBusy(null);
    if (!target) return;
    setPreviewVersion((v) => v + 1);
    setPreviewOpen(true);
  };

  const handleIssueClick = async () => {
    if (isDirty || !invoice) {
      setBusy('issue');
      const saved = await save({ quiet: true });
      setBusy(null);
      if (!saved) return;
    }
    setConfirmIssue(true);
  };

  const doIssue = async () => {
    setBusy('issue');
    try {
      const issued = await invoiceService.issue(invoice.id);
      toast.success(`Invoice ${issued.number} issued`);
      setSavedSnapshot(snapshot);
      navigate(`/billing/invoices/${issued.id}`);
    } catch (error) {
      const fields = getApiFieldErrors(error);
      setErrors((prev) => ({ ...prev, fields: { ...prev.fields, ...fields } }));
      toast.error(getApiError(error, 'Failed to issue the invoice'));
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    setBusy('delete');
    try {
      await invoiceService.delete(invoice.id);
      toast.success('Draft deleted');
      setSavedSnapshot(snapshot);
      navigate('/billing/invoices');
    } catch (error) {
      toast.error(getApiError(error, 'Failed to delete the draft'));
    } finally {
      setBusy(null);
    }
  };

  /* ---------------------------------------------------------------- */

  const pendingProjects = client?.pendingProjects ?? [];
  const tickedIds = useMemo(() => new Set(items.map((line) => line.projectId).filter(Boolean)), [items]);
  const allTicked = pendingProjects.length > 0 && pendingProjects.every((p) => tickedIds.has(p.id));
  const stateMissing = Boolean(client) && (totals ? totals.stateMissing : !client.stateCode);
  const formErrors = errors.fields;
  const topLevelErrors = [formErrors.invoice, formErrors.items, formErrors.billing_client_id].filter(Boolean);

  if (loading) {
    return (
      <div className="bl-center">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="bl-page invoice-editor">
      <button type="button" className="bl-back" onClick={() => leave(invoice ? `/billing/invoices/${invoice.id}` : '/billing/invoices')}>
        <FiArrowLeft /> {invoice ? 'Back to invoice' : 'Back to invoices'}
      </button>

      <div className="ie-header">
        <div className="ie-title">
          <h1>{invoice ? 'Edit draft invoice' : 'New invoice'}</h1>
          <div className="ie-title-meta">
            <Badge variant="default" size="small">Draft</Badge>
            {isDirty ? (
              <span className="ie-dirty">Unsaved changes</span>
            ) : (
              invoice && <span className="bl-muted">All changes saved</span>
            )}
          </div>
        </div>
        <div className="bl-header-actions">
          {invoice && (
            <Button variant="ghost" icon={<FiTrash2 />} onClick={() => setConfirmDelete(true)} disabled={Boolean(busy)}>
              Delete
            </Button>
          )}
          <Button variant="outline" icon={<FiEye />} onClick={handlePreview} loading={busy === 'preview'} disabled={!client || Boolean(busy)}>
            Preview PDF
          </Button>
          <Button variant="secondary" icon={<FiSave />} onClick={handleSave} loading={busy === 'save'} disabled={!client || Boolean(busy)}>
            Save draft
          </Button>
          <Button
            icon={<FiSend />}
            onClick={handleIssueClick}
            loading={busy === 'issue'}
            disabled={!client || !items.length || stateMissing || Boolean(busy)}
            title={stateMissing ? 'Add a GSTIN or state to the client first' : undefined}
          >
            Issue invoice
          </Button>
        </div>
      </div>

      {topLevelErrors.length > 0 && (
        <div className="bl-banner danger" role="alert">
          <FiAlertTriangle />
          <div className="bl-banner-body">{topLevelErrors.map((msg) => <div key={msg}>{msg}</div>)}</div>
        </div>
      )}

      <div className="ie-layout">
        <div className="ie-main">
          <Card title="Client" padding="medium">
            {(!client || changingClient) ? (
              <div className="bl-form-stack">
                <ClientSearchAutocomplete
                  autoFocus
                  onSelect={handleSelectClient}
                  onCreateNew={(name) => setClientModal({ mode: 'create', name })}
                />
                {busy === 'client' && <span className="bl-muted">Loading client…</span>}
                {changingClient && (
                  <div>
                    <Button variant="ghost" size="small" onClick={() => setChangingClient(false)}>
                      Keep {client.name}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="ie-client">
                <dl className="bl-info-grid">
                  <div className="bl-span-all">
                    <dt>Bill to</dt>
                    <dd className="ie-client-name">{client.name}</dd>
                  </div>
                  <div>
                    <dt>GST IN</dt>
                    <dd className="bl-mono">{client.gstin || '—'}</dd>
                  </div>
                  <div>
                    <dt>Client ID</dt>
                    <dd className="bl-mono">{client.clientCode || '—'}</dd>
                  </div>
                  <div>
                    <dt>State</dt>
                    <dd>{client.stateCode ? `${client.stateCode}${client.stateName ? ` — ${client.stateName}` : ''}` : '—'}</dd>
                  </div>
                  <div className="bl-span-all">
                    <dt>Address</dt>
                    <dd>{client.address || '—'}</dd>
                  </div>
                </dl>

                <div className="ie-client-foot">
                  {totals?.taxLabel && !stateMissing && <Badge variant="primary">{totals.taxLabel}</Badge>}
                  <div className="ie-client-actions">
                    {!client.unavailable && (
                      <Button variant="ghost" size="small" icon={<FiEdit2 />} onClick={() => setClientModal({ mode: 'edit' })}>
                        Edit client
                      </Button>
                    )}
                    <Button variant="outline" size="small" onClick={() => setChangingClient(true)}>
                      Change client
                    </Button>
                  </div>
                </div>

                {client.unavailable && (
                  <div className="bl-banner warning">
                    <FiAlertTriangle />
                    <div className="bl-banner-body">This client has been deleted. The details above are from the draft.</div>
                  </div>
                )}

                {stateMissing && (
                  <div className="bl-banner warning">
                    <FiAlertTriangle />
                    <div className="bl-banner-body">
                      Client state unknown — add a GSTIN or state before issuing.{' '}
                      {!client.unavailable && (
                        <button type="button" className="bl-link" onClick={() => setClientModal({ mode: 'edit' })}>
                          Edit client
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {client && (
            <Card
              title="Pending projects"
              subtitle={pendingProjects.length ? 'Ticked projects are added as lines' : undefined}
              padding="medium"
              action={
                pendingProjects.length > 0 && (
                  <Button variant="ghost" size="small" icon={allTicked ? <FiSquare /> : <FiCheckSquare />} onClick={() => setAllProjects(!allTicked)}>
                    {allTicked ? 'Untick all' : 'Tick all'}
                  </Button>
                )
              }
            >
              {pendingProjects.length === 0 ? (
                <div className="bl-empty">This client has no pending projects. Add ad-hoc lines below.</div>
              ) : (
                <ul className="ie-projects">
                  {pendingProjects.map((project) => (
                    <li key={project.id}>
                      <label className={`ie-project ${tickedIds.has(project.id) ? 'is-checked' : ''}`}>
                        <input type="checkbox" checked={tickedIds.has(project.id)} onChange={() => toggleProject(project)} />
                        <span className="ie-project-text">
                          <span className="ie-project-desc">{project.description}</span>
                          <span className="bl-cell-sub">
                            {[project.designCode, project.hsnCode && `HSN ${project.hsnCode}`].filter(Boolean).join(' · ') || '—'}
                          </span>
                        </span>
                        <span className="ie-project-amount bl-amount">{formatINR(project.taxableAmount)}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          <Card title="Line items" subtitle="Drag or use the arrows to reorder. Order = SR No. on the invoice." padding="medium">
            <InvoiceLineItemsTable items={items} onChange={setItems} rowErrors={errors.rows} defaultHsn={defaults.hsn} />
          </Card>

          <Card title="Internal notes" padding="medium">
            <CmsField
              id="ie-notes"
              type="textarea"
              rows={3}
              placeholder="Visible to admins only; not printed on the invoice."
              value={form.notes}
              onChange={(v) => setField('notes', v)}
              error={formErrors.notes}
            />
          </Card>
        </div>

        <aside className="ie-side">
          <Card title="Invoice details" padding="medium">
            <div className="bl-form-stack">
              <div className="ie-number">
                <span className="cf-label">Invoice No.</span>
                <span className="ie-number-value">{invoice?.number || 'Assigned on issue'}</span>
              </div>
              <CmsField
                id="ie-date"
                type="date"
                label="Invoice date"
                required
                value={form.invoiceDate}
                onChange={(v) => setField('invoiceDate', v)}
                error={formErrors.invoice_date}
                hint="Decides the financial year of the number."
              />
              <CmsField
                id="ie-due"
                type="date"
                label="Due date"
                value={effectiveDueDate}
                onChange={(v) => setField('dueDate', v)}
                error={formErrors.due_date}
                hint={form.dueTouched ? undefined : `Invoice date + ${defaults.dueDays} days`}
                inputProps={{ min: form.invoiceDate }}
              />
              <details className="ie-advanced" open={Boolean(form.gstRate) || Boolean(formErrors.gst_rate)}>
                <summary>Advanced</summary>
                <div className="ie-advanced-body">
                  <CmsField
                    id="ie-gst"
                    type="number"
                    label="GST rate override (%)"
                    placeholder={`Default ${defaults.gstRate}%`}
                    value={form.gstRate}
                    onChange={(v) => setField('gstRate', v)}
                    error={formErrors.gst_rate}
                    hint="Leave blank to use the default rate from Billing Settings."
                    inputProps={{ min: 0, max: 100, step: '0.01' }}
                  />
                </div>
              </details>
            </div>
          </Card>

          <Card title="Totals" padding="medium" className="ie-totals-card">
            <InvoiceTotalsPanel totals={client ? totals : null} loading={totalsLoading} error={totalsError} />
          </Card>
        </aside>
      </div>

      <ClientFormModal
        isOpen={Boolean(clientModal)}
        onClose={() => setClientModal(null)}
        quick={clientModal?.mode === 'create'}
        initialName={clientModal?.name ?? ''}
        client={clientModal?.mode === 'edit' ? client : null}
        onSaved={handleClientSaved}
      />

      <ConfirmDialog
        isOpen={Boolean(pendingSwitch)}
        onClose={() => setPendingSwitch(null)}
        onConfirm={() => applyClient(pendingSwitch, defaults.hsn)}
        title="Change client?"
        message={`Lines from ${client?.name}'s projects will be replaced with ${pendingSwitch?.name}'s pending projects. Ad-hoc lines are kept.`}
        confirmText="Change client"
        variant="primary"
      />

      <ConfirmDialog
        isOpen={confirmIssue}
        onClose={() => setConfirmIssue(false)}
        onConfirm={doIssue}
        title="Issue this invoice?"
        message={`This assigns the next invoice number and locks the invoice${totals?.totalAmount ? ` for ${formatINR(totals.totalAmount)}` : ''}. Corrections require cancelling it.`}
        confirmText="Issue invoice"
        variant="primary"
        loading={busy === 'issue'}
      />

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={doDelete}
        title="Delete draft"
        message="Delete this draft invoice? Its projects stay pending. This cannot be undone."
        confirmText="Delete draft"
        loading={busy === 'delete'}
      />

      <ConfirmDialog
        isOpen={Boolean(leaveTarget)}
        onClose={() => setLeaveTarget(null)}
        onConfirm={() => {
          const to = leaveTarget;
          setSavedSnapshot(snapshot);
          navigate(to);
        }}
        title="Discard unsaved changes?"
        message="You have changes that haven't been saved. Leave without saving?"
        confirmText="Discard changes"
      />

      <Modal isOpen={previewOpen} onClose={() => setPreviewOpen(false)} title="Invoice preview" size="xlarge">
        {previewOpen && invoice && <InvoicePdfFrame invoiceId={invoice.id} version={previewVersion} />}
      </Modal>
    </div>
  );
};

export default InvoiceEditorPage;
